/**
 * Label Schema Panel — Multi-Schema Editor
 *
 * Visual editor for mapping label segments to topology fields.
 * Supports multiple schemas, each with its own level assignment.
 *
 * Workflow:
 *   1. User adds schemas (or they are loaded from project)
 *   2. Each schema: sample label → character range segments → level
 *   3. Live preview: match count + sample field extractions per schema
 *   4. Apply All: compiles all schemas → patterns, re-classifies entities
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useProjectStore } from '../../stores/projectStore';
import {
  compileSchema,
  compileSchemas,
  testSchemaPattern,
  autoDetectSegments,
  suggestLevelFromSegments,
} from '../../domain/layout/labelSchemaCompiler';
import { reclassifyAndBind } from '../../domain/layout/parentBind';
import type {
  LabelSchema,
  LabelSchemaSegment,
} from '../../domain/layout/types';
import './LabelSchemaPanel.css';

/** Standard topology field options */
const STANDARD_FIELDS: Array<{ value: string; label: string }> = [
  { value: 'station', label: 'Station' },
  { value: 'inverter', label: 'Inverter' },
  { value: 'mppt', label: 'MPPT' },
  { value: 'dccb', label: 'DCCB' },
  { value: 'string', label: 'String' },
  { value: 'module', label: 'Module' },
  { value: 'skip', label: 'Skip' },
];

const STANDARD_FIELD_VALUES = new Set(STANDARD_FIELDS.map((f) => f.value));

const FIELD_COLORS: Record<string, string> = {
  station: '#ef4444',
  inverter: '#3b82f6',
  mppt: '#06b6d4',
  dccb: '#f97316',
  string: '#22c55e',
  module: '#a855f7',
  skip: '#6b7280',
};

const STANDARD_LEVEL_OPTIONS: Array<{ value: string; label: string; color: string }> = [
  { value: 'station', label: 'Station', color: '#ef4444' },
  { value: 'inverter', label: 'Inverter', color: '#3b82f6' },
  { value: 'mppt', label: 'MPPT', color: '#06b6d4' },
  { value: 'dccb', label: 'DCCB', color: '#f97316' },
  { value: 'string', label: 'String', color: '#22c55e' },
  { value: 'module', label: 'Module', color: '#a855f7' },
];

const STANDARD_LEVEL_VALUES = new Set(STANDARD_LEVEL_OPTIONS.map((o) => o.value));

/** Default color palette for new custom levels */
const CUSTOM_LEVEL_PALETTE = [
  '#e11d48', '#0ea5e9', '#84cc16', '#f59e0b', '#8b5cf6', '#ec4899',
  '#14b8a6', '#f43f5e', '#6366f1', '#10b981',
];

function customFieldColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const h = ((hash % 360) + 360) % 360;
  return `hsl(${h}, 65%, 55%)`;
}

function getFieldColor(field: string): string {
  return FIELD_COLORS[field] ?? customFieldColor(field);
}

function segmentAtIndex(segments: LabelSchemaSegment[], idx: number): LabelSchemaSegment | undefined {
  return segments.find((s) => idx >= s.start && idx < s.end);
}

// ── Per-Schema Editor Card ───────────────────────────────────

interface SchemaCardProps {
  schema: LabelSchema;
  index: number;
  entities: any[];
  onUpdate: (index: number, schema: LabelSchema) => void;
  onRemove: (index: number) => void;
}

function SchemaCard({ schema, index, entities, onUpdate, onRemove }: SchemaCardProps) {
  const [dragStart, setDragStart] = useState<number | null>(null);
  const [dragEnd, setDragEnd] = useState<number | null>(null);
  const isDragging = useRef(false);
  const [customEditSegIdx, setCustomEditSegIdx] = useState<number | null>(null);
  const [customEditValue, setCustomEditValue] = useState('');
  const [customLevelEdit, setCustomLevelEdit] = useState(false);
  const [customLevelName, setCustomLevelName] = useState('');

  const { sampleLabel, segments, level } = schema;
  const isCustomLevel = !STANDARD_LEVEL_VALUES.has(level);

  // Collect custom field names
  const customFieldsInUse = useMemo(() => {
    const customs: string[] = [];
    for (const seg of segments) {
      if (seg.field && !STANDARD_FIELD_VALUES.has(seg.field) && !customs.includes(seg.field)) {
        customs.push(seg.field);
      }
    }
    return customs;
  }, [segments]);

  // Test pattern
  const testResult = useMemo(() => {
    if (!sampleLabel || segments.length === 0) return null;
    try {
      const pattern = compileSchema({ sampleLabel, segments, level });
      return testSchemaPattern(pattern, entities);
    } catch {
      return null;
    }
  }, [sampleLabel, segments, level, entities]);

  // Compiled regex for display
  const compiledRegex = useMemo(() => {
    if (!sampleLabel || segments.length === 0) return '';
    try { return compileSchema({ sampleLabel, segments, level }).regex; } catch { return ''; }
  }, [sampleLabel, segments, level]);

  // ── Handlers ──

  const handleSampleChange = useCallback((newLabel: string) => {
    const newSegments = autoDetectSegments(newLabel);
    const suggestedLevel = suggestLevelFromSegments(newSegments);
    onUpdate(index, { sampleLabel: newLabel, segments: newSegments, level: suggestedLevel });
  }, [index, onUpdate]);

  const handleLevelChange = useCallback((newLevel: string, color?: string) => {
    const isCustom = !STANDARD_LEVEL_VALUES.has(newLevel);
    onUpdate(index, {
      ...schema,
      level: newLevel,
      customLevelColor: isCustom ? (color ?? schema.customLevelColor ?? CUSTOM_LEVEL_PALETTE[index % CUSTOM_LEVEL_PALETTE.length]) : undefined,
    });
    setCustomLevelEdit(false);
  }, [index, schema, onUpdate]);

  const updateSegments = useCallback((newSegments: LabelSchemaSegment[]) => {
    const suggestedLevel = suggestLevelFromSegments(newSegments);
    onUpdate(index, { ...schema, segments: newSegments, level: suggestedLevel });
  }, [index, schema, onUpdate]);

  // Drag selection
  const handleCharMouseDown = useCallback((idx: number) => {
    isDragging.current = true;
    setDragStart(idx);
    setDragEnd(idx);
  }, []);

  const handleCharMouseEnter = useCallback((idx: number) => {
    if (isDragging.current) setDragEnd(idx);
  }, []);

  const handleMouseUp = useCallback(() => {
    if (!isDragging.current || dragStart === null || dragEnd === null) {
      isDragging.current = false;
      return;
    }
    isDragging.current = false;
    const start = Math.min(dragStart, dragEnd);
    const end = Math.max(dragStart, dragEnd) + 1;

    const nonOverlapping = segments.filter((s) => s.end <= start || s.start >= end);
    const newSegments = [...nonOverlapping, { field: 'skip' as const, start, end }]
      .sort((a, b) => a.start - b.start);
    updateSegments(newSegments);

    setDragStart(null);
    setDragEnd(null);
  }, [dragStart, dragEnd, segments, updateSegments]);

  useEffect(() => {
    const handler = () => { if (isDragging.current) handleMouseUp(); };
    window.addEventListener('mouseup', handler);
    return () => window.removeEventListener('mouseup', handler);
  }, [handleMouseUp]);

  // Segment field change
  const handleSegmentFieldChange = useCallback((segIndex: number, field: string) => {
    if (field === '__custom__') {
      setCustomEditSegIdx(segIndex);
      setCustomEditValue('');
      return;
    }
    const updated = [...segments];
    updated[segIndex] = { ...updated[segIndex], field };
    updateSegments(updated);
  }, [segments, updateSegments]);

  const handleCustomNameConfirm = useCallback((segIndex: number) => {
    const name = customEditValue.trim();
    if (name) {
      const updated = [...segments];
      updated[segIndex] = { ...updated[segIndex], field: name };
      updateSegments(updated);
    }
    setCustomEditSegIdx(null);
    setCustomEditValue('');
  }, [customEditValue, segments, updateSegments]);

  const handleRemoveSegment = useCallback((segIndex: number) => {
    updateSegments(segments.filter((_, i) => i !== segIndex));
    setCustomEditSegIdx(null);
  }, [segments, updateSegments]);

  const handleToggleFlexible = useCallback((segIndex: number) => {
    const updated = [...segments];
    updated[segIndex] = { ...updated[segIndex], flexible: !updated[segIndex].flexible };
    updateSegments(updated);
  }, [segments, updateSegments]);

  const selStart = dragStart !== null && dragEnd !== null ? Math.min(dragStart, dragEnd) : -1;
  const selEnd = dragStart !== null && dragEnd !== null ? Math.max(dragStart, dragEnd) : -1;

  return (
    <div className="lsp__card">
      {/* Header: index + remove */}
      <div className="lsp__card-header">
        <span className="lsp__card-index">Schema {index + 1}</span>
        <button
          type="button"
          className="lsp__card-remove"
          onClick={() => onRemove(index)}
          title="Remove schema"
        >&times;</button>
      </div>

      {/* Sample label input */}
      <div className="lsp__row">
        <label className="lsp__label">Sample Label</label>
        <input
          className="lsp__input"
          value={sampleLabel}
          onChange={(e) => handleSampleChange(e.target.value)}
          placeholder="e.g. 1.2.DCCB12.STR12"
        />
      </div>

      {/* Level picker */}
      <div style={{ marginTop: 4 }}>
        <label className="lsp__label">Level</label>
        <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap', marginTop: 2, alignItems: 'center' }}>
          {STANDARD_LEVEL_OPTIONS.map((opt) => {
            const isActive = level === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                style={{
                  padding: '2px 6px', fontSize: 10, borderRadius: 3, cursor: 'pointer',
                  background: isActive ? opt.color + '33' : 'transparent',
                  border: `1px solid ${isActive ? opt.color : 'rgba(255,255,255,0.12)'}`,
                  color: isActive ? opt.color : 'var(--sl-text-muted, #9ca3af)',
                  fontWeight: isActive ? 600 : 400,
                }}
                onClick={() => handleLevelChange(opt.value)}
              >
                {opt.label}
              </button>
            );
          })}
          {/* Custom level button or active custom chip */}
          {isCustomLevel && !customLevelEdit ? (
            <button
              type="button"
              style={{
                padding: '2px 6px', fontSize: 10, borderRadius: 3, cursor: 'pointer',
                background: (schema.customLevelColor ?? '#e11d48') + '33',
                border: `1px solid ${schema.customLevelColor ?? '#e11d48'}`,
                color: schema.customLevelColor ?? '#e11d48',
                fontWeight: 600,
              }}
              onClick={() => { setCustomLevelEdit(true); setCustomLevelName(level); }}
              title="Click to edit custom level"
            >
              {level}
            </button>
          ) : customLevelEdit ? (
            <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <input
                style={{
                  width: 60, padding: '2px 4px', fontSize: 10,
                  background: 'rgba(255,255,255,0.1)', border: '1px solid var(--sl-accent, #13d6c6)',
                  borderRadius: 3, color: 'var(--sl-text, #e5e7eb)', outline: 'none',
                }}
                value={customLevelName}
                onChange={(e) => setCustomLevelName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && customLevelName.trim()) {
                    handleLevelChange(customLevelName.trim().toLowerCase());
                  }
                  if (e.key === 'Escape') setCustomLevelEdit(false);
                }}
                placeholder="Name..."
                autoFocus
              />
              <input
                type="color"
                value={schema.customLevelColor ?? CUSTOM_LEVEL_PALETTE[index % CUSTOM_LEVEL_PALETTE.length]}
                onChange={(e) => handleLevelChange(
                  customLevelName.trim().toLowerCase() || level,
                  e.target.value,
                )}
                title="Pick level color"
                style={{ width: 22, height: 22, padding: 0, border: 'none', cursor: 'pointer', background: 'transparent' }}
              />
            </div>
          ) : (
            <button
              type="button"
              style={{
                padding: '2px 6px', fontSize: 10, borderRadius: 3, cursor: 'pointer',
                background: 'transparent',
                border: '1px dashed rgba(255,255,255,0.2)',
                color: 'var(--sl-text-muted, #9ca3af)',
              }}
              onClick={() => { setCustomLevelEdit(true); setCustomLevelName(''); }}
              title="Define a custom level"
            >
              Custom...
            </button>
          )}
        </div>
        {/* Color picker for active custom level */}
        {isCustomLevel && !customLevelEdit && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 3 }}>
            <span style={{ fontSize: 9, color: 'var(--sl-text-muted)' }}>Color:</span>
            <input
              type="color"
              value={schema.customLevelColor ?? CUSTOM_LEVEL_PALETTE[index % CUSTOM_LEVEL_PALETTE.length]}
              onChange={(e) => onUpdate(index, { ...schema, customLevelColor: e.target.value })}
              style={{ width: 20, height: 20, padding: 0, border: 'none', cursor: 'pointer', background: 'transparent' }}
            />
          </div>
        )}
      </div>

      {/* Character map */}
      {sampleLabel.length > 0 && (
        <>
          <div className="lsp__label" style={{ marginTop: 6 }}>Select text to define segments</div>
          <div
            className="lsp__charmap"
            onMouseLeave={() => { if (!isDragging.current) { setDragStart(null); setDragEnd(null); } }}
          >
            {sampleLabel.split('').map((char, i) => {
              const seg = segmentAtIndex(segments, i);
              const inDrag = i >= selStart && i <= selEnd && selStart >= 0;
              const color = seg ? getFieldColor(seg.field) : undefined;
              return (
                <span
                  key={i}
                  className={
                    'lsp__char' +
                    (seg ? ' lsp__char--assigned' : '') +
                    (inDrag ? ' lsp__char--dragging' : '')
                  }
                  style={seg ? { borderBottomColor: color, color } : undefined}
                  onMouseDown={(e) => { e.preventDefault(); handleCharMouseDown(i); }}
                  onMouseEnter={() => handleCharMouseEnter(i)}
                >
                  {char}
                </span>
              );
            })}
          </div>
        </>
      )}

      {/* Segment chips */}
      {segments.length > 0 && (
        <div className="lsp__segments" style={{ marginTop: 4 }}>
          {segments.map((seg, i) => {
            const text = sampleLabel.slice(seg.start, seg.end);
            const color = getFieldColor(seg.field);
            const isCustomField = !STANDARD_FIELD_VALUES.has(seg.field);
            const isEditingCustom = customEditSegIdx === i;

            return (
              <div key={`${seg.start}-${seg.end}`} className="lsp__segment">
                <div className="lsp__segment-chip" style={{ borderColor: color, color }}>
                  {text}
                  <button
                    type="button"
                    className={`lsp__segment-flex${seg.flexible ? ' lsp__segment-flex--on' : ''}`}
                    onClick={() => handleToggleFlexible(i)}
                    title={seg.flexible ? 'Flexible: matches any alphanumeric value (click to disable)' : 'Strict match (click for flexible — any alphanumeric)'}
                  >*</button>
                  <button
                    type="button"
                    className="lsp__segment-remove"
                    onClick={() => handleRemoveSegment(i)}
                    title="Remove segment"
                  >&times;</button>
                </div>
                {isEditingCustom ? (
                  <div className="lsp__custom-input-wrap">
                    <input
                      className="lsp__custom-input"
                      value={customEditValue}
                      onChange={(e) => setCustomEditValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleCustomNameConfirm(i);
                        if (e.key === 'Escape') setCustomEditSegIdx(null);
                      }}
                      onBlur={() => handleCustomNameConfirm(i)}
                      placeholder="Name..."
                      autoFocus
                    />
                  </div>
                ) : (
                  <select
                    className="lsp__segment-select"
                    value={isCustomField ? seg.field : seg.field}
                    onChange={(e) => handleSegmentFieldChange(i, e.target.value)}
                    style={{ color }}
                  >
                    {STANDARD_FIELDS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                    {customFieldsInUse.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                    <option value="__custom__">Custom...</option>
                  </select>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pattern preview */}
      {compiledRegex && (
        <div className="lsp__regex" style={{ marginTop: 4 }}>
          <code>{compiledRegex}</code>
        </div>
      )}

      {/* Match count */}
      {testResult && (
        <div className="lsp__match-count" style={{ marginTop: 4 }}>
          Matches: <strong>{testResult.matchCount}</strong> / {testResult.totalEntities}
        </div>
      )}
    </div>
  );
}

// ── Main Panel ───────────────────────────────────────────────

export default function LabelSchemaPanel() {
  const siteLayout = useProjectStore((s) => s.data.siteLayout);
  const setLabelSchemas = useProjectStore((s) => s.setLabelSchemas);
  const updateConfig = useProjectStore((s) => s.updateSiteLayoutConfig);
  const updateEntities = useProjectStore((s) => s.updateSiteLayoutEntities);

  const [schemas, setSchemas] = useState<LabelSchema[]>([]);
  const [initialized, setInitialized] = useState(false);

  // Load schemas from project data on mount
  useEffect(() => {
    if (initialized || !siteLayout) return;

    if (siteLayout.labelSchemas && siteLayout.labelSchemas.length > 0) {
      setSchemas(siteLayout.labelSchemas);
    } else {
      // Auto-detect initial schema from the longest string-level entity
      const stringEntities = siteLayout.textEntities.filter(
        (e) => e.classification?.level === 'string',
      );
      if (stringEntities.length > 0) {
        let best = stringEntities[0];
        let bestParts = best.normalizedText.split('.').length;
        for (const e of stringEntities) {
          const parts = e.normalizedText.split('.').length;
          if (parts > bestParts) {
            best = e;
            bestParts = parts;
          }
        }
        const label = best.normalizedText;
        const segments = autoDetectSegments(label);
        const level = suggestLevelFromSegments(segments);
        setSchemas([{ sampleLabel: label, segments, level }]);
      }
    }
    setInitialized(true);
  }, [siteLayout, initialized]);

  const entities = siteLayout?.textEntities ?? [];

  const handleUpdateSchema = useCallback((index: number, schema: LabelSchema) => {
    setSchemas((prev) => {
      const updated = [...prev];
      updated[index] = schema;
      return updated;
    });
  }, []);

  const handleRemoveSchema = useCallback((index: number) => {
    setSchemas((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleAddSchema = useCallback(() => {
    // Try to pick an unclassified entity as the sample
    let sample = '';
    if (siteLayout) {
      const unclassified = siteLayout.textEntities.filter(
        (e) => !e.classification && !e.classificationOverride && e.normalizedText.length > 2,
      );
      if (unclassified.length > 0) {
        // Pick the most common-length unclassified label
        const longest = unclassified.reduce((a, b) =>
          b.normalizedText.split('.').length > a.normalizedText.split('.').length ? b : a,
        );
        sample = longest.normalizedText;
      }
    }
    const segments = sample ? autoDetectSegments(sample) : [];
    const level = segments.length > 0 ? suggestLevelFromSegments(segments) : 'string';
    setSchemas((prev) => [...prev, { sampleLabel: sample, segments, level }]);
  }, [siteLayout]);

  const handleApplyAll = useCallback(() => {
    if (!siteLayout) return;

    const compiled = compileSchemas(schemas.filter((s) => s.sampleLabel && s.segments.length > 0));
    const existingConfig = (siteLayout.classificationConfig || [])
      .filter((p) => !p.id.startsWith('schema-auto-'));
    const mergedConfig = [...compiled, ...existingConfig];
    const reclassified = reclassifyAndBind(siteLayout.textEntities, mergedConfig);

    setLabelSchemas(schemas);
    updateConfig(mergedConfig);
    updateEntities(reclassified, { reclassified: true });
  }, [schemas, siteLayout, setLabelSchemas, updateConfig, updateEntities]);

  const handleResetAll = useCallback(() => {
    if (!siteLayout) return;

    const existingConfig = (siteLayout.classificationConfig || [])
      .filter((p) => !p.id.startsWith('schema-auto-'));
    const reclassified = reclassifyAndBind(siteLayout.textEntities, existingConfig);

    setLabelSchemas([]);
    updateConfig(existingConfig);
    updateEntities(reclassified, { reclassified: true });

    setSchemas([]);
    setInitialized(false);
  }, [siteLayout, setLabelSchemas, updateConfig, updateEntities]);

  if (!siteLayout) return null;

  const hasActiveSchemas = (siteLayout.labelSchemas?.length ?? 0) > 0;
  const validSchemas = schemas.filter((s) => s.sampleLabel && s.segments.length > 0);

  return (
    <div className="lsp">
      {/* Schema list */}
      {schemas.map((schema, i) => (
        <SchemaCard
          key={i}
          schema={schema}
          index={i}
          entities={entities}
          onUpdate={handleUpdateSchema}
          onRemove={handleRemoveSchema}
        />
      ))}

      {/* Add schema button */}
      <button
        type="button"
        className="lsp__btn"
        onClick={handleAddSchema}
        style={{ fontSize: 11 }}
      >
        + Add Schema
      </button>

      {/* Actions */}
      {schemas.length > 0 && (
        <div className="lsp__actions">
          <button
            type="button"
            className="lsp__btn lsp__btn--apply"
            onClick={handleApplyAll}
            disabled={validSchemas.length === 0}
          >
            Apply All & Re-classify
          </button>
          {hasActiveSchemas && (
            <button
              type="button"
              className="lsp__btn lsp__btn--reset"
              onClick={handleResetAll}
            >
              Reset All
            </button>
          )}
        </div>
      )}
    </div>
  );
}
